import type { ErrorTrackerProps } from "@similarweb/sentry-logger";
import Sentry from "@similarweb/sentry-logger";
import type { Primitive } from "@similarweb/sw-track";

import { SI_SENTRY_TAGS } from "app/pages/sales-intelligence/constants/sentry";

import { is } from "../type-guards";

type TrackingSentryTags = {
    solution?: string;
    component?: string;
    [key: string]: Primitive | undefined;
};

type TrackingInterceptorParams = {
    errorMessage: string;
    tags?: TrackingSentryTags;
} & Partial<Omit<ErrorTrackerProps, "message" | "e">>;

export const sentryErrorTrackerWithScope = ({
    errorMessage,
    e,
    tags,
    ...rest
}: TrackingInterceptorParams & { e: Error }): void => {
    if (is.undefined(tags)) {
        Sentry.errorTracker({
            message: errorMessage,
            e,
            level: "error",
            ...rest,
        });
    } else {
        const outputTags: TrackingSentryTags = { ...tags, solution: SI_SENTRY_TAGS.solution };

        Sentry.withScope((scope) => {
            scope.setTags(outputTags);

            Sentry.errorTracker({
                message: errorMessage,
                e,
                level: "error",
                ...rest,
            });
        });
    }
};

export const createSentryTrackingInterceptor =
    ({ errorMessage, tags }: TrackingInterceptorParams) =>
    <T>(callbackFn: () => Promise<T>): Promise<T> => {
        try {
            return callbackFn();
        } catch (e) {
            sentryErrorTrackerWithScope({ e, errorMessage, tags });

            throw e;
        }
    };
